import React from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  Text,
} from "recharts";
import { Row, Col } from "antd";
import styled from "styled-components";

const Customstyle = styled.div`
  .title {
    color: #636980;
  }
  .rows {
    display: flex;
    justify-content: space-between;
  }
  .col-label {
    color: #636980;
  }
  .col-value {
    margin-left: 40px;
    width: 98px;
    height: 18px;
    gap: 0px;
    opacity: 0px;
  }
  .text {
    font-size: 16px;
    font-weight: 400;
  }
  .footer {
    position: fixed;
    bottom: 0;
    right: 0;
    width: 78%;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 12px 54px;
    background: white;
    box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.1);
  }
  .footer-button {
    width: 100px;
    height: 35px;
    font-size: 16px;
    border-radius: 8px;
  }
  .parent-container {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }

  .averagescore {
    width: 200px;
    height: 35px;
    border-radius: 8px;
    padding-right: 10px;
    padding-top: 5px;
    color: #636980;
    background-color: #f4f4f4;
    display: flex;
    justify-content: flex-end;
  }
  .score {
    font-weight: 500;
  }
  .matrix {
    height: 226px;
  }
  .matrixright {
    margin-top: 5px;
    font-size: 14px;
    font-weight: 400px;
    color: #636980;
    line-height: 10.26px;
    padding-top: 20px;
  }
  .recommendtext {
    color: #636980;
  }
  .count {
    color: black;
  }
  .appraiser {
    display: flex;
    gap: 300px;
  }
  .final {
    margin-top: -5px;
  }
`;

// Define grid colors with labels
const borderedAreas = [
  { x1: 0, x2: 2, y1: 4, y2: 6, fill: "#FFC000", label: "6" },
  { x1: 2, x2: 4, y1: 4, y2: 6, fill: "#00B050", label: "3" },
  { x1: 4, x2: 6, y1: 4, y2: 6, fill: "#7030A0", label: "1" },
  { x1: 0, x2: 2, y1: 2, y2: 4, fill: "#FF0000", label: "8" },
  { x1: 2, x2: 4, y1: 2, y2: 4, fill: "#FFC000", label: "5" },
  { x1: 4, x2: 6, y1: 2, y2: 4, fill: "#00B050", label: "2" },
  { x1: 0, x2: 2, y1: 0, y2: 2, fill: "#FF0000", label: "9" },
  { x1: 2, x2: 4, y1: 0, y2: 2, fill: "#FF0000", label: "7" },
  { x1: 4, x2: 6, y1: 0, y2: 2, fill: "#FFC000", label: "4" },
];

function PerformanceMatrix({ Xaxis, Yaxis }) {
  const data = [{ x: Xaxis, y: Yaxis }];
  console.log("Xaxis:", Xaxis, "Yaxis:", Yaxis);
  return (
    <Customstyle>
      {/* <h6>Performance Matrix</h6> */}
      <br />
      <Row gutter={24} style={{ marginBottom: "50px" }}>
        <Col xs={24} md={12}>
          <div className="matrix">
            <ScatterChart
              width={500}
              height={300}
              margin={{ top: 20, right: 20, bottom: 40, left: 40 }}
            >
              <CartesianGrid stroke="#ccc" />
              <XAxis
                type="number"
                dataKey="x"
                name="Soft Skills"
                domain={[0, 6]}
                tickCount={4}
                label={{
                  value: "<------Soft Skills------>",
                  position: "bottom",
                  dy: -20,
                }}
                tick={false}
                axisLine={{ stroke: "#ddd" }}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Technical Skills"
                domain={[0, 6]}
                tickCount={4}
                label={{
                  value: "<------Technical Skills------>",
                  angle: -90,
                  position: "insideLeft",
                  dx: 30, // Moves label slightly left for better alignment
                  dy: 0, // Centers the label vertically
                  style: { textAnchor: "middle" }, // Ensures text is centered
                }}
                tick={false}
                axisLine={{ stroke: "#ddd" }}
              />

              <Tooltip cursor={{ strokeDasharray: "3 3" }} />

              {/* Render Grid with Labels */}
              {borderedAreas.map((area, index) => (
                <ReferenceArea
                  x1={area.x1}
                  x2={area.x2}
                  y1={area.y1}
                  y2={area.y2}
                  fill={area.fill}
                  stroke="#fff"
                  strokeWidth={2}
                  label={{
                    position: "insideBottomRight",
                    value: `${area.label}`,
                    fill: "#fff",
                    fontSize: 16,
                  }}
                />
              ))}

              {/* Black Dot for a Data Point */}
              <Scatter data={data} fill="black" shape="circle" />
            </ScatterChart>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="matrixright">
            <p>
              <span className="count">1</span>&nbsp;&nbsp; Star (High
              Competence, High Commitment)
            </p>
            <p>
              <span className="count">2</span>&nbsp;&nbsp; High Potential
              (Moderate Competence, High Commitment)
            </p>
            <p>
              <span className="count">3</span>&nbsp;&nbsp; Top Performer (High
              Competence, Moderate Commitment)
            </p>
            <p>
              <span className="count">4</span>&nbsp;&nbsp; Dedicated Learner
              (Low Competence, High Commitment)
            </p>
            <p>
              <span className="count">5</span>&nbsp;&nbsp; Repable Performer
              (Moderate Competence, Moderate Commitment)
            </p>
            <p>
              <span className="count">6</span>&nbsp;&nbsp; Skilled but
              Unmotivated (High Competence, Low Commitment)
            </p>
            <p>
              <span className="count">7</span>&nbsp;&nbsp;&nbsp; Inconsistent
              Performer (Low Competence, Moderate Commitment)
            </p>
            <p>
              <span className="count">8</span>&nbsp;&nbsp; Under-performer
              (Moderate Competence, Low Commitment)
            </p>
            <p>
              <span className="count">9</span>&nbsp;&nbsp; Disengaged (Low
              Competence, Low Commitment)
            </p>
          </div>
        </Col>
      </Row>
    </Customstyle>
  );
}

export default PerformanceMatrix;
